import { forwardRef, useRef, useImperativeHandle } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { getFormActions } from '../../../utils/form-helpers';
import stripeFormConfig, { canSubmit } from '../../../payment/stripe-form-config';
import EmailSuggestion from '../../../components/EmailSuggestion';
import styles from './stripe-form.module.less';
import Input2 from '../../../components/input/input-2/Input2';
import { isAnyRewardNeitherLotteryNorProductReward } from '../cart/cart-model';
import { useI18nTexts } from '../../i18n/useI18nTexts';
import {
    EMAIL_ADDRESS_IS_USED_FOR_TEXT_KEY,
    ERROR_IN_EMAIL_ADDRESS_TEXT_KEY,
    EMAIL_TEXT_KEY,
    CARD_DETAILS_TEXT_KEY,
} from '../../i18n/project-i18n-model';
import { useProject } from '../../project-hooks/useProject';
import { getCardErrorFromStripeError } from './stripeUtils';

const stripeFormActions = getFormActions(stripeFormConfig);

const StripeForm = forwardRef(({ initiateCallback, rewards }, ref) => {
    const dispatch = useDispatch();
    const project = useProject();
    const formRef = useRef();
    const stripe = useStripe();
    const elements = useElements();

    const stripeForm = useSelector((state) => state.stripeForm);

    useImperativeHandle(ref, () => ({
        submit: () => {
            formRef.current?.requestSubmit(); // HTML5 way to submit a form
        },
    }));

    const texts = useI18nTexts([
        EMAIL_ADDRESS_IS_USED_FOR_TEXT_KEY,
        ERROR_IN_EMAIL_ADDRESS_TEXT_KEY,
        CARD_DETAILS_TEXT_KEY,
        EMAIL_TEXT_KEY,
    ]);

    const handleSubmit = async (ev) => {
        ev.preventDefault();

        dispatch(stripeFormActions.setFormField('isProcessingPayment', true));

        const { errors: submitErrors } = await elements.submit();

        if (submitErrors) {
            dispatch(stripeFormActions.setFormField('isProcessingPayment', false));
            dispatch(
                stripeFormActions.setFormField('errors', {
                    card: getCardErrorFromStripeError(project, submitErrors),
                })
            );
        } else {
            const { error, confirmationToken } = await stripe.createConfirmationToken({
                elements,
                params: {
                    return_url: window.location.href,
                },
            });

            if (error) {
                dispatch(stripeFormActions.setFormField('isProcessingPayment', false));
                dispatch(
                    stripeFormActions.setFormField('errors', {
                        ...stripeForm.errors,
                    })
                );
            } else if (canSubmit(stripeForm)) {
                initiateCallback({
                    confirmationTokenId: confirmationToken.id,
                    email: stripeForm.email,
                    stripe,
                });
            }
        }
    };

    const { email, errors } = stripeForm;

    const emailText = `${texts[EMAIL_ADDRESS_IS_USED_FOR_TEXT_KEY]} ${
        isAnyRewardNeitherLotteryNorProductReward({ rewards })
            ? 'og for å gi den til innsamler for at du skal kunne kontaktes om belønningen din'
            : ''
    }`;

    return (
        <form ref={formRef} onSubmit={handleSubmit} className={styles.cardDetails}>
            <PaymentElement className={[styles.stripeElement, errors?.card && 'ffe-input-field--invalid'].join(' ')} />
            <Input2
                wrapperClass={styles.emailInput}
                title={texts[EMAIL_TEXT_KEY]}
                fieldDescription={emailText}
                fieldName="email"
                value={email}
                error={errors?.email ? texts[ERROR_IN_EMAIL_ADDRESS_TEXT_KEY] : false}
                type="email"
                onChange={(e) => dispatch(stripeFormActions.setFormField('email', e.target.value.trim()))}
                onBlur={(e) => dispatch(stripeFormActions.setFormField('email', e.target.value.trim(), true))}
                extraMargin={false}
            />
            <EmailSuggestion className={styles.emailSuggestion} email={email} />
        </form>
    );
});

StripeForm.propTypes = {
    initiateCallback: PropTypes.func.isRequired,
    rewards: PropTypes.array.isRequired,
};

StripeForm.displayName = 'StripeForm';

export default StripeForm;
